import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import Select from "react-select";
import { getDoctorsAction } from "../../../store/actions/DoctorsAction";
import { getBranchesAction } from "../../../store/actions/BranchAction";
import { getBookedAppointmentsAction } from "../../../store/actions/appoinmentAction";
import {
  createReceptionistAction,
  addSearchPatientQueueAction,
  rescheduleAppointmentAction,
} from "../../../store/actions/ReceptionistAction";
import { getAvailableSlotsAction } from "../../../store/actions/AssignedAppointmentAction";
import { formatName } from "../../../utils/formatName";
import defaultAvatar from "../../../../src/assets/images/placeholder.jpg";
import enqueue from "../../../../src/assets/images/enque.png";
import Reshedule from "../../../../src/assets/images/deadline.png";
import cancel from "../../../../src/assets/images/cancel_app.png";
import axiosInstance from "../../../services/AxiosInstance";

// ======================= Utility Functions =======================
const getTodayDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const convertTo24Hour = (time12h) => {
  if (!time12h) return "00:00";
  const match = time12h.match(/(\d{1,2}):(\d{2})(AM|PM)/i);
  if (!match) return "00:00";
  let [, hours, minutes, meridian] = match;
  hours = parseInt(hours, 10);
  if (meridian.toUpperCase() === "PM" && hours !== 12) hours += 12;
  if (meridian.toUpperCase() === "AM" && hours === 12) hours = 0;
  return `${hours.toString().padStart(2, "0")}:${minutes}`;
};

const createLocalDate = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return new Date(NaN);
  const converted = convertTo24Hour(timeStr);
  const [hours, minutes] = converted.split(":").map(Number);
  if (isNaN(hours) || isNaN(minutes)) return new Date(NaN);
  const [year, month, day] = dateStr.split("-").map(Number);
  if (!year || !month || !day) return new Date(NaN);
  return new Date(year, month - 1, day, hours, minutes, 0);
};

const isRescheduleAllowed = (appointmentDate, appointmentTime) => {
  if (!appointmentDate || !appointmentTime) return false;
  const appointmentDateTime = createLocalDate(appointmentDate, appointmentTime);
  const currentTime = new Date();
  const diffHours = (appointmentDateTime - currentTime) / (1000 * 60 * 60);
  return !isNaN(diffHours) && diffHours > 48;
};

// ======================= Main Component =======================
const Appointmentlist = () => {
  const dispatch = useDispatch();
  const [selectedDate, setSelectedDate] = useState(getTodayDateString());
  const [selectedDoctorId, setSelectedDoctorId] = useState("");
  const [enqueuedPatientIds, setEnqueuedPatientIds] = useState([]);

  // Loading states
  const [enqueueLoading, setEnqueueLoading] = useState({});
  const [cancelLoading, setCancelLoading] = useState({});
  const [fetchingDetails, setFetchingDetails] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [rescheduleLoading, setRescheduleLoading] = useState(false);

  // Modal state (unified for booking)
  const [showModal, setShowModal] = useState(false);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [selectedPatientForBooking, setSelectedPatientForBooking] = useState(null);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [modalDoctorId, setModalDoctorId] = useState("");
  const [modalDate, setModalDate] = useState(getTodayDateString());
  const [modalTimeSlot, setModalTimeSlot] = useState(null);
  const [modalBranch, setModalBranch] = useState(null);
  const [modalMode, setModalMode] = useState("Online");
  const [slotsData, setSlotsData] = useState([]);
  const [doctorOptions, setDoctorOptions] = useState([]);

  // Reschedule modal state
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [selectedAppointmentForReschedule, setSelectedAppointmentForReschedule] = useState(null);
  const [rescheduleDate, setRescheduleDate] = useState(getTodayDateString());
  const [rescheduleBranch, setRescheduleBranch] = useState(null);
  const [rescheduleMode, setRescheduleMode] = useState("Online");

  // Redux state
  const { bookedAppointments, loading: appointmentLoading } = useSelector(
    (state) => state.appointment || { bookedAppointments: [], loading: false }
  );
  const doctors = useSelector((state) => state.doctors.doctors || []);
  const branches = useSelector((state) => state.branches?.branches || []);
  const { availableSlots } = useSelector((state) => state.assignedAppointments || {});

  // ======================= Effects =======================
  useEffect(() => {
    dispatch(getDoctorsAction(false));
    dispatch(getBranchesAction());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDate) {
      dispatch(getBookedAppointmentsAction(selectedDate, selectedDoctorId || null));
    }
  }, [selectedDate, selectedDoctorId, dispatch]);

  useEffect(() => {
    if (!modalDoctorId || !modalDate) return;
    dispatch(getAvailableSlotsAction({ doctor_id: modalDoctorId, date: modalDate }));
  }, [modalDoctorId, modalDate, dispatch]);

  useEffect(() => {
    if (!availableSlots || availableSlots.length === 0) {
      setSlotsData([]);
      setModalTimeSlot(null);
      return;
    }
    const formatted = availableSlots.map((slot) => ({
      value: slot.time_slot,
      label: slot.time_slot,
      confirmed: Number(slot.confirmed_appointments || 0),
      max: Number(slot.max_appointments || 0),
      available: slot.available,
      is_full: slot.is_full,
    }));
    setSlotsData(formatted);
    const firstAvailable = formatted.find((slot) => slot.available);
    setModalTimeSlot(firstAvailable || null);
  }, [availableSlots]);

  useEffect(() => {
    const opts = doctors.map((doc) => ({
      value: doc.doctor_id,
      label: doc.doctor_name,
      specialization: doc.specialization,
    }));
    setDoctorOptions(opts);
  }, [doctors]);

  // ======================= Helper: Fetch appointment details =======================
  const fetchAppointmentDetailsForPatient = async (patientId) => {
    setFetchingDetails(true);
    try {
      const response = await axiosInstance.post("/patient/appointmentDetails", {
        patient_id: patientId,
      });
      const data = response?.data?.data ?? null;
      setAppointmentDetails(data);
      return data;
    } catch (error) {
      console.error("Error fetching appointment details:", error);
      setAppointmentDetails(null);
      return null;
    } finally {
      setFetchingDetails(false);
    }
  };

  // ======================= Core Enqueue Flow =======================
  const handleEnqueue = async (appointment) => {
    const patientId = appointment.patient_id;
    if (enqueueLoading[patientId]) return;
    setEnqueueLoading((prev) => ({ ...prev, [patientId]: true }));

    try {
      const qrcodescanData = JSON.stringify({
        id: String(patientId),
        name: formatName(appointment.name),
        mobile: appointment.whatsapp_contact_no || appointment.phone || "",
      });
      const queueDetails = await dispatch(
        createReceptionistAction(qrcodescanData, appointment.doctor_id)
      );
      if (!queueDetails) throw new Error("Failed to enqueue");

      const appointmentData = await fetchAppointmentDetailsForPatient(patientId);
      const today = getTodayDateString();

      if (appointmentData && queueDetails.is_confirmed === "Yes" && appointmentData.date === today) {
        setEnqueuedPatientIds((prev) => [...prev, patientId]);
        await dispatch(getBookedAppointmentsAction(selectedDate, selectedDoctorId || null));
        return;
      }

      setSelectedPatientForBooking({
        patient_id: patientId,
        name: appointment.name,
        doctor_id: appointment.doctor_id,
      });
      setModalDoctorId(appointmentData?.doctor_id || appointment.doctor_id || "");
      setModalDate(appointmentData?.date || today);
      setModalTimeSlot(null);
      setModalBranch(null);
      setModalMode(appointmentData?.mode || "Online");
      setAppointmentDetails(appointmentData);
      setSelectedAppointment(appointmentData ? appointment : null);
      setShowModal(true);
    } catch (err) {
      console.error("Enqueue flow error:", err);
      Swal.fire("Error", "Failed to process patient", "error");
    } finally {
      setEnqueueLoading((prev) => ({ ...prev, [patientId]: false }));
    }
  };

  // ======================= Confirm Booking (not reschedule) =======================
  const handleConfirmBooking = async () => {
    if (!modalDoctorId) {
      Swal.fire("Warning", "Please select a doctor", "warning");
      return;
    }
    if (!modalDate) {
      Swal.fire("Warning", "Please select a date", "warning");
      return;
    }
    if (!modalTimeSlot) {
      Swal.fire("Warning", "Please select a time slot", "warning");
      return;
    }
   
    setModalLoading(true);
    try {
      if (selectedAppointment) {
        // Legacy reschedule (with time slot) – kept for compatibility
        const payload = {
          appointment_id: Number(selectedAppointment.id || selectedAppointment.appointment_id),
          date: modalDate,
         
      
          time_slot: modalTimeSlot.value,
        };
        const res = await dispatch(rescheduleAppointmentAction(payload));
        if (res?.status === "success") {
          Swal.fire("Success", "Appointment rescheduled successfully!", "success");
        } else {
          throw new Error(res?.message || "Reschedule failed");
        }
      } else {
        if (!selectedPatientForBooking?.patient_id) throw new Error("Patient not selected");
        await dispatch(
          addSearchPatientQueueAction(
            selectedPatientForBooking.patient_id,
            modalDoctorId,
            modalTimeSlot.value,
            modalDate,
            modalBranch.value
          )
        );
        Swal.fire("Success", "Appointment booked successfully!", "success");
      }

      setShowModal(false);
      setSelectedAppointment(null);
      setSelectedPatientForBooking(null);
      setAppointmentDetails(null);
      setModalDoctorId("");
      setModalDate(getTodayDateString());
      setModalTimeSlot(null);
      setModalBranch(null);
      await dispatch(getBookedAppointmentsAction(selectedDate, selectedDoctorId || null));
    } catch (err) {
      console.error("Booking/reschedule error:", err);
      Swal.fire("Error", err.message || "Operation failed", "error");
    } finally {
      setModalLoading(false);
    }
  };

  // ======================= RESCHEDULE HANDLER (with pre‑fill fix) =======================
  const handleRescheduleClick = async (appointment) => {
    const timeSlot = appointment.timeSlot || appointment.time_slot;
    const [timePart] = timeSlot?.split(" to ") || [];
    if (!timePart) {
      Swal.fire("Error", "Missing time slot", "error");
      return;
    }

    // 1. Reschedule count check
    if (parseInt(appointment.rescheduled_cnt) >= 1) {
      Swal.fire({ icon: "warning", title: "Not Allowed", text: "Already rescheduled once." });
      return;
    }

    // 2. 48-hour check
    if (!isRescheduleAllowed(appointment.date, timePart)) {
      Swal.fire({
        icon: "warning",
        title: "Cannot Reschedule",
        html: `Only allowed >48h before.<br/>📅 ${appointment.date} ${timePart}`,
      });
      return;
    }

    // 3. Enqueue check
    const isPresented =
      appointment.date === getTodayDateString() &&
      (appointment.queueDetails?.is_presented === "Yes" ||
        appointment.is_presented === "Yes" ||
        enqueuedPatientIds.includes(appointment.patient_id));

    if (isPresented) {
      Swal.fire({ icon: "info", title: "Cannot Reschedule", text: "Patient already enqueued for today." });
      return;
    }

    // 4. Pre‑fill modal data with corrections
    setSelectedAppointmentForReschedule(appointment);
    setRescheduleDate(appointment.date || getTodayDateString());

    // Normalise mode (API returns "OFFLINE" / "ONLINE")
    let normalizedMode = "Online";
    if (appointment.mode) {
      normalizedMode = appointment.mode.toLowerCase() === "offline" ? "Offline" : "Online";
    }
    setRescheduleMode(normalizedMode);

    // Pre‑fill branch – find matching branch object
    const branchOption = branches.find(
      (b) => String(b.branch_id) === String(appointment.branch_id)
    );
    setRescheduleBranch(
      branchOption ? { value: branchOption.branch_id, label: branchOption.branch_name } : null
    );

    setShowRescheduleModal(true);
  };

  const handleRescheduleConfirm = async () => {
    if (!rescheduleDate || !rescheduleBranch) {
      Swal.fire("Validation Error", "Please fill all reschedule fields (Date, Branch)", "error");
      return;
    }
    const payload = {
      appointment_id: Number(selectedAppointmentForReschedule?.appointment_id),
      date: rescheduleDate,
      mode: rescheduleMode,
      branch: rescheduleBranch?.value || "",
    };
    setRescheduleLoading(true);
    try {
      const res = await dispatch(rescheduleAppointmentAction(payload));
      if (res?.status === "success") {
        Swal.fire("Success", res.message || "Appointment rescheduled successfully!", "success");
        setShowRescheduleModal(false);
        await dispatch(getBookedAppointmentsAction(selectedDate, selectedDoctorId || null));
      } else {
        throw new Error(res?.message || "Failed to reschedule");
      }
    } catch (err) {
      console.error("Reschedule error:", err);
      Swal.fire("Error", err.message || "Failed to reschedule", "error");
    } finally {
      setRescheduleLoading(false);
    }
  };

  // ======================= Cancel Appointment =======================
  const handleRemove = async (appointment) => {
    const appointmentId = appointment.id || appointment.appointment_id;
    if (cancelLoading[appointmentId]) return;
    const result = await Swal.fire({
      title: "Remove Appointment",
      text: `Remove ${appointment.name}'s appointment?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, remove",
    });
    if (result.isConfirmed) {
      setCancelLoading((prev) => ({ ...prev, [appointmentId]: true }));
      try {
        // TODO: Replace with actual cancel API endpoint
        await dispatch(getBookedAppointmentsAction(selectedDate, selectedDoctorId || null));
        Swal.fire("Removed", "Appointment removed", "success");
      } catch (error) {
        Swal.fire("Error", "Failed to remove", "error");
      } finally {
        setCancelLoading((prev) => ({ ...prev, [appointmentId]: false }));
      }
    }
  };

  // ======================= Render Helpers =======================
  const getPatientAvatar = (patient) => {
    if (patient.patient_photo && patient.patient_photo !== "") return patient.patient_photo;
    return defaultAvatar;
  };

  const doctorSelectOptions = doctors.map((doc) => ({
    value: doc.doctor_id,
    label: doc.doctor_name,
    specialization: doc.specialization,
  }));

  const selectedDoctorOption = doctorSelectOptions.find(
    (opt) => String(opt.value) === String(selectedDoctorId)
  );

  const formatOptionLabel = ({ label, specialization }) => (
    <div>
      <div style={{ fontWeight: "bold" }}>{label}</div>
      <div style={{ fontSize: "0.8em", color: "#666" }}>{specialization}</div>
    </div>
  );

  return (
    <div>
      <div className="card border-0 shadow-sm" style={{ borderRadius: "10px" }}>
        <div className="card-body p-4">
          {/* Header Filters */}
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
            <h4 className="mb-0" style={{ fontSize: "1.4rem", fontWeight: 600 }}>
              Booked Appointments
            </h4>
            <div className="d-flex gap-3 align-items-center flex-wrap">
              <div style={{ minWidth: "250px" }}>
                <Select
                  value={selectedDoctorOption}
                  onChange={(opt) => setSelectedDoctorId(opt?.value || "")}
                  options={doctorSelectOptions}
                  formatOptionLabel={formatOptionLabel}
                  placeholder="Select a doctor..."
                  isClearable
                  styles={{ control: (base) => ({ ...base, minHeight: "50px" }) }}
                />
              </div>
              <div style={{ minWidth: "180px", position: "relative" }}>
                <input
                  type="date"
                  className="form-control"
                  value={selectedDate}
                  min={getTodayDateString()}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  style={{ paddingRight: "40px", height: "50px" }}
                />
                <i
                  className="fa fa-calendar"
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    pointerEvents: "none",
                    fontSize: "18px",
                    color: "#555",
                  }}
                />
              </div>
            </div>
          </div>

          {/* Appointments List */}
          {appointmentLoading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              {bookedAppointments?.length > 0 ? (
                <div style={{ maxHeight: "500px", overflowY: "auto" }}>
                  <div className="row g-3">
                    {bookedAppointments.map((appointment) => {
                      const appointmentId = appointment.id || appointment.appointment_id;
                      const patientId = appointment.patient_id;
                      const isPresented =
                        appointment.date === getTodayDateString() &&
                        (appointment.queueDetails?.is_presented === "Yes" ||
                          appointment.is_presented === "Yes" ||
                          enqueuedPatientIds.includes(patientId));

                      const timeSlot = appointment.timeSlot || appointment.time_slot;
                      const [timePart] = timeSlot?.split(" to ") || [];
                      const alreadyRescheduled = parseInt(appointment.rescheduled_cnt) >= 1;
                      const within48Hours = timePart && !isRescheduleAllowed(appointment.date, timePart);
                      const canReschedule = !isPresented && !alreadyRescheduled && !within48Hours && timePart;

                      const showRescheduleDisabledMsg = () => {
                        if (isPresented)
                          Swal.fire({ icon: "info", title: "Cannot Reschedule", text: "Already enqueued" });
                        else if (alreadyRescheduled)
                          Swal.fire({ icon: "info", title: "Not Allowed", text: "Already rescheduled once" });
                        else if (within48Hours) {
                          const diff = (createLocalDate(appointment.date, timePart) - new Date()) / (1000 * 60 * 60);
                          Swal.fire({
                            icon: "warning",
                            title: "Cannot Reschedule",
                            html: `Only allowed >48h before.<br/>📅 ${appointment.date} ${timePart}<br/>${Math.floor(diff)} hours remaining`,
                          });
                        }
                      };

                      return (
                        <div key={appointmentId} className="col-12 col-md-6 col-xl-4">
                          <div
                            style={{
                              backgroundColor: "#ffffff",
                              borderRadius: "12px",
                              padding: "12px",
                              border: "1px solid #e9ecef",
                              transition: "box-shadow 0.2s",
                              height: "100%",
                            }}
                          >
                            <div className="d-flex align-items-center justify-content-between w-100">
                              <div className="d-flex align-items-center flex-grow-1">
                                <div className="me-3">
                                  <img
                                    src={getPatientAvatar(appointment)}
                                    alt={appointment.name}
                                    width="48"
                                    height="48"
                                    className="rounded-circle"
                                    style={{ objectFit: "cover" }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = defaultAvatar;
                                    }}
                                  />
                                </div>
                                <div className="flex-grow-1">
                                  <h6 className="mb-1" style={{ fontSize: "15px", fontWeight: 600 }}>
                                    {appointment.name}
                                    {appointment.diseases && (
                                      <span className="text-muted" style={{ fontWeight: 400 }}>
                                        {" "}
                                        – {appointment.diseases}
                                      </span>
                                    )}
                                  </h6>
                                  <p className="mb-0 text-muted" style={{ fontSize: "13px" }}>
                                    {appointment.doctor}
                                    {appointment.timeSlot && <> | {appointment.timeSlot}</>}
                                    <span className="ms-1"> | {appointment.mode}</span>
                                    {appointment.branch && <span className="ms-1"> | {appointment.branch}</span>}
                                  </p>
                                  {parseInt(appointment.rescheduled_cnt) > 0 && (
                                    <span className="badge bg-info text-white mt-1" style={{ fontSize: "10px" }}>
                                      Rescheduled: {appointment.rescheduled_cnt}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {/* Cancel */}
                                {cancelLoading[appointmentId] ? (
                                  <div style={{ width: "28px", height: "28px" }}>
                                    <div className="spinner-border spinner-border-sm text-danger" />
                                  </div>
                                ) : (
                                  <img
                                    src={cancel}
                                    alt="Cancel"
                                    width={28}
                                    height={28}
                                    style={{ cursor: "pointer", opacity: isPresented ? 0.5 : 1 }}
                                    onClick={() => !isPresented && handleRemove(appointment)}
                                    title={isPresented ? "Already enqueued" : "Cancel Appointment"}
                                  />
                                )}

                                {/* Reschedule */}
                                {canReschedule ? (
                                  <img
                                    src={Reshedule}
                                    alt="Reschedule"
                                    width={28}
                                    height={28}
                                    style={{ cursor: "pointer" }}
                                    onClick={() => handleRescheduleClick(appointment)}
                                    title="Reschedule Appointment"
                                  />
                                ) : (
                                  <img
                                    src={Reshedule}
                                    alt="Reschedule Disabled"
                                    width={28}
                                    height={28}
                                    style={{ opacity: 0.5, cursor: "pointer" }}
                                    onClick={showRescheduleDisabledMsg}
                                    title="Cannot reschedule"
                                  />
                                )}

                                {/* Enqueue / Book Appointment */}
                                {!isPresented && (
                                  <>
                                    {enqueueLoading[patientId] ? (
                                      <div style={{ width: "28px", height: "28px" }}>
                                        <div className="spinner-border spinner-border-sm text-primary" />
                                      </div>
                                    ) : (
                                      <img
                                        src={enqueue}
                                        alt="Enqueue"
                                        width={28}
                                        height={28}
                                        style={{ cursor: "pointer" }}
                                        onClick={() => handleEnqueue(appointment)}
                                        title="Enqueue / Book Appointment"
                                      />
                                    )}
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <p className="text-muted">No appointments found</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ======================= Modal for Booking / Legacy Reschedule ======================= */}
      <Modal show={showModal} onHide={() => !modalLoading && setShowModal(false)} centered size="lg">
        <Modal.Header closeButton={!modalLoading}>
          <Modal.Title>
            {selectedAppointment ? "Book Appointment" : "Book Appointment"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {fetchingDetails && (
            <div className="alert alert-warning mb-4">
              <i className="fas fa-spinner fa-spin me-2"></i> Loading appointment details...
            </div>
          )}

          {selectedAppointment && appointmentDetails && (
            <div className="alert alert-warning mb-4">
              <h6 className="mb-3">
                <i className="fas fa-calendar-check me-2"></i> Current Appointment Details
              </h6>
              <div className="row">
                {appointmentDetails.doctor_name && appointmentDetails.doctor_name !== "null" && (
                  <div className="col-md-6 mb-2">
                    <small className="text-muted d-block">Doctor</small>
                    <strong>{appointmentDetails.doctor_name}</strong>
                  </div>
                )}
                {appointmentDetails.date && appointmentDetails.date !== "null" && (
                  <div className="col-md-6 mb-2">
                    <small className="text-muted d-block">Date</small>
                    <strong>{appointmentDetails.date}</strong>
                  </div>
                )}
                {appointmentDetails.time_slot && appointmentDetails.time_slot !== "null" && (
                  <div className="col-md-6 mb-2">
                    <small className="text-muted d-block">Time Slot</small>
                    <strong>{appointmentDetails.time_slot}</strong>
                  </div>
                )}
                {appointmentDetails.is_confirmed && appointmentDetails.is_confirmed !== "null" && (
                  <div className="col-md-6 mb-2">
                    <small className="text-muted d-block">Status</small>
                    <strong>
                      <span
                        className={`badge ${
                          appointmentDetails.is_confirmed === "Yes" ? "bg-success" : "bg-warning"
                        }`}
                      >
                        {appointmentDetails.is_confirmed === "Yes" ? "Confirmed" : "Not Confirmed"}
                      </span>
                    </strong>
                  </div>
                )}
              </div>
              <small className="text-muted d-block mt-2">Select new details below to proceed</small>
            </div>
          )}

          {/* Row for Date and Doctor */}
          <div className="row">
            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">
                Select Date <span className="text-danger">*</span>
              </label>
              <input
                type="date"
                className="form-control"
                value={modalDate}
                min={getTodayDateString()}
                onChange={(e) => {
                  setModalDate(e.target.value);
                  setModalTimeSlot(null);
                }}
                disabled={modalLoading}
                style={{ height: "45px" }}
              />
            </div>

            <div className="col-md-6 mb-3">
              <label className="form-label fw-bold">
                Select Doctor <span className="text-danger">*</span>
              </label>
              <Select
                options={doctorOptions}
                value={doctorOptions.find((opt) => opt.value === modalDoctorId)}
                onChange={(opt) => {
                  setModalDoctorId(opt?.value || "");
                  setModalTimeSlot(null);
                }}
                placeholder="Search and select doctor..."
                formatOptionLabel={formatOptionLabel}
                isClearable
                isDisabled={modalLoading}
                styles={{ control: (base) => ({ ...base, minHeight: "45px" }) }}
              />
              {!modalDoctorId && (
                <small className="text-warning mt-1 d-block">
                  <i className="fas fa-exclamation-triangle me-1"></i> Please select a doctor
                </small>
              )}
            </div>
          </div>

     

          {/* Mode Selection - FIXED (was missing) */}
       

          {/* Time Slot Selection */}
          <div className="mb-3">
            <label className="form-label fw-bold">
              Select Time Slot <span className="text-danger">*</span>
            </label>
            {!modalDoctorId ? (
              <small className="text-muted">Please select doctor first</small>
            ) : slotsData.length === 0 ? (
              <small className="text-muted">No available slots for selected doctor and date</small>
            ) : (
              <div className="row">
                {slotsData.map((slot) => (
                  <div className="col-md-6 mb-2" key={slot.value}>
                    <div
                      className={`border rounded p-2 d-flex justify-content-between align-items-center ${
                        modalTimeSlot?.value === slot.value ? "border-primary bg-light" : ""
                      }`}
                    >
                      <div className="form-check">
                        <input
                          type="radio"
                          className="form-check-input"
                          name="timeslot"
                          disabled={slot.is_full}
                          checked={modalTimeSlot?.value === slot.value}
                          onChange={() => setModalTimeSlot(slot)}
                        />
                        <label className="form-check-label fw-bold">{slot.label}</label>
                      </div>
                      <span className="badge bg-info">
                        {slot.confirmed}/{slot.max}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Booking Summary */}
          {modalDoctorId && modalDate && modalTimeSlot && modalBranch && (
            <div className="alert alert-success mt-4">
              <h6 className="alert-heading fw-bold">Booking Summary</h6>
              <hr className="my-2" />
              <p className="mb-1">
                <strong>Doctor:</strong>{" "}
                {doctorOptions.find((d) => d.value === modalDoctorId)?.label}
              </p>
              <p className="mb-1">
                <strong>Date:</strong>{" "}
                {new Date(modalDate).toLocaleDateString("en-US", {
                  weekday: "long",
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </p>
              <p className="mb-1">
                <strong>Time:</strong> {modalTimeSlot.label}
              </p>
              <p className="mb-0">
                <strong>Branch:</strong> {modalBranch.label} | <strong>Mode:</strong> {modalMode}
              </p>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <button className="btn btn-danger" onClick={() => setShowModal(false)} disabled={modalLoading}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleConfirmBooking}
            disabled={!modalDoctorId || !modalDate || !modalTimeSlot || modalLoading}
          >
            {modalLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Processing...
              </>
            ) : (
              "Confirm Booking"
            )}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ======================= Reschedule Modal (pre‑fills correctly) ======================= */}
      <Modal show={showRescheduleModal} onHide={() => !rescheduleLoading && setShowRescheduleModal(false)} centered>
        <Modal.Header closeButton={!rescheduleLoading}>
          <Modal.Title>Reschedule Appointment</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label">Date</label>
            <input
              type="date"
              className="form-control"
              value={rescheduleDate}
              min={getTodayDateString()}
              onChange={(e) => setRescheduleDate(e.target.value)}
              disabled={rescheduleLoading}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Branch</label>
            <Select
              value={rescheduleBranch}
              onChange={setRescheduleBranch}
              options={branches.map((branch) => ({
                value: branch.branch_id || branch.value,
                label: branch.branch_name || branch.label,
              }))}
              placeholder="Select Branch"
              styles={{ control: (base) => ({ ...base, minHeight: "45px" }) }}
              isDisabled={rescheduleLoading}
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Mode</label>
            <select
              className="form-select"
              value={rescheduleMode}
              onChange={(e) => setRescheduleMode(e.target.value)}
              disabled={rescheduleLoading}
              style={{ height: "45px" }}
            >
              <option value="Online">Online</option>
              <option value="Offline">Offline</option>
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setShowRescheduleModal(false)}
            disabled={rescheduleLoading}
          >
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleRescheduleConfirm}
            disabled={rescheduleLoading}
          >
            {rescheduleLoading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Rescheduling...
              </>
            ) : (
              "Reschedule"
            )}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Appointmentlist;